import { ReactNode, useRef, useState } from 'react'
import {
  ButtonType,
  IconPaths,
  IconSize,
  KTSVG,
  showConfirmAlert,
  showError,
} from '../../../_metronic/helpers'
import styles from './ItemList.module.css'

export type ItemListProps<T> = {
  items: T[]
  renderItem: (item: T) => ReactNode
  itemKey?: (item: T, index: number) => string | number
  onEdit?: (item: T) => void | Promise<void>
  onDelete?: (item: T) => void | Promise<void>
  confirmDelete?: boolean
  confirmDeleteOptions?: Parameters<typeof showConfirmAlert>[0]
  emptyContent?: ReactNode
  wrapperClassName?: string
  itemClassName?: string
  actionsClassName?: string
  editButtonClassName?: string
  deleteButtonClassName?: string
  editAriaLabel?: string
  deleteAriaLabel?: string
}

export const ItemList = <T,>({
  items,
  renderItem,
  itemKey,
  onEdit,
  onDelete,
  confirmDelete = true,
  confirmDeleteOptions,
  emptyContent = null,
  wrapperClassName = 'd-flex flex-column gap-2',
  itemClassName = 'd-flex align-items-center gap-3 px-3 py-2',
  actionsClassName = 'd-flex align-items-center gap-2',
  editButtonClassName = 'btn btn-icon w-30px h-30px',
  deleteButtonClassName = `btn btn-icon w-30px h-30px ${styles.deleteButton}`,
  editAriaLabel = 'Editar item',
  deleteAriaLabel = 'Remover item',
}: ItemListProps<T>): JSX.Element => {
  const [pendingKeys, setPendingKeys] = useState<Set<string | number>>(new Set())
  const pendingKeysRef = useRef(pendingKeys)

  if (!items.length) {
    return <>{emptyContent}</>
  }

  const withPending = async (key: string | number, action: () => Promise<void>) => {
    if (pendingKeysRef.current.has(key)) return
    pendingKeysRef.current = new Set(pendingKeysRef.current).add(key)
    setPendingKeys(pendingKeysRef.current)
    try {
      await action()
    } finally {
      pendingKeysRef.current = new Set(pendingKeysRef.current)
      pendingKeysRef.current.delete(key)
      setPendingKeys(pendingKeysRef.current)
    }
  }

  const handleEdit = (item: T, key: string | number) =>
    withPending(key, async () => {
      try {
        await onEdit?.(item)
      } catch (error) {
        showError(error)
      }
    })

  const handleDelete = (item: T, key: string | number) =>
    withPending(key, async () => {
      if (confirmDelete) {
        const isConfirmed = await showConfirmAlert(confirmDeleteOptions)
        if (!isConfirmed) return
      }
      try {
        await onDelete?.(item)
      } catch (error) {
        showError(error)
      }
    })

  return (
    <div className={wrapperClassName}>
      {items.map((item, index) => {
        const key = itemKey ? itemKey(item, index) : index
        const isPending = pendingKeys.has(key)

        return (
          <div key={key} className={itemClassName}>
            {renderItem(item)}

            {(onEdit || onDelete) && (
              <div className={actionsClassName}>
                {onEdit && (
                  <button
                    type={ButtonType.Button}
                    className={editButtonClassName}
                    onClick={() => handleEdit(item, key)}
                    aria-label={editAriaLabel}
                    title={editAriaLabel}
                    disabled={isPending}
                  >
                    <KTSVG
                      path={IconPaths.Pen}
                      size={IconSize.ExtraSmall}
                      svgClassName='mh-15px'
                      className='m-0'
                    />
                  </button>
                )}

                {onDelete && (
                  <button
                    type={ButtonType.Button}
                    className={deleteButtonClassName}
                    onClick={() => handleDelete(item, key)}
                    aria-label={deleteAriaLabel}
                    title={deleteAriaLabel}
                    disabled={isPending}
                  >
                    <KTSVG
                      path={IconPaths.Trash}
                      size={IconSize.ExtraSmall}
                      svgClassName='mh-15px'
                      className='m-0'
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
